import React from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import Markdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
// Sherpa sends guidance links as bare URLs on their own line, deliberately —
// the same replies are consumed by clients that do not render Markdown at all.
// Plain CommonMark leaves a bare URL as inert text, so without this plugin a
// guidance link arrives visible but unclickable. It also enables the table
// syntax this component already styles below but has never been able to render.
import remarkGfm from "remark-gfm";
import SherpaIcon from "../../assets/Sherpa.svg";
import { getInternalGuidancePath } from "../../utils/guidanceNavigation.js";

const descriptions = {
  stress: [
    "Not stressed",
    "A small amount",
    "Stressed",
    "Feeling pressured",
    "Stress is negatively affecting my work",
  ],
  mood: ["Very low", "Low", "Neutral", "Content", "Happy"],
  sleep: ["Sleep deprived", "Tired", "Neutral", "Okay", "Well rested"],
};

const quickDomains = [
  "Physical",
  "Emotional",
  "Acceptance",
  "Relationship",
  "Loving-Kindness",
  "Intelectual",
];

const ChatMessage = ({ message, onQuickReply, onReport, userAvatar, disabled = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUser = message.role === "user";
  const flags = message.flags || [];

  const oneToFiveLabels = flags.includes("stress")
    ? descriptions.stress
    : flags.includes("sleep")
    ? descriptions.sleep
    : descriptions.mood;

  return (
    <Box sx={{ py: 0.9 }}>
      <Stack
        direction={isUser ? "row-reverse" : "row"}
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box
          sx={{
            maxWidth: { xs: "84%", sm: "76%", md: "70%" },
            ml: isUser ? 4 : 0,
            mr: !isUser ? 4 : 0,
          }}
        >
          <Stack
            direction={isUser ? "row-reverse" : "row"}
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 0.6 }}
          >
            <Avatar
              src={isUser ? userAvatar || "/pro.png" : SherpaIcon}
              alt={isUser ? "You" : "Private mentor"}
              sx={{ width: 24, height: 24 }}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#2A8062" }}>
              {isUser ? "You" : "Private mentor"}
            </Typography>
          </Stack>
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
              bgcolor: isUser ? "#0C8F63" : "#FFFFFF",
              color: isUser ? "primary.contrastText" : "text.primary",
              border: isUser ? "none" : "1px solid",
              borderColor: "#E0EEE7",
              boxShadow: isUser ? "none" : "0 3px 12px rgba(22, 74, 56, 0.05)",
            }}
          >
            <Box
              sx={{
                "& p": { my: 0.5, lineHeight: 1.65, fontSize: "0.95rem" },
                "& pre": {
                  overflowX: "auto",
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                },
                "& code": { fontFamily: "monospace" },
                "& a": {
                  color: "#0C8F63",
                  fontWeight: 600,
                  overflowWrap: "anywhere",
                },
                "& table": { borderCollapse: "collapse", width: "100%" },
                "& th, & td": {
                  border: 1,
                  borderColor: "divider",
                  px: 1,
                  py: 0.5,
                },
              }}
            >
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, href, children, ...props }) => {
                    const guidancePath = !isUser
                      ? getInternalGuidancePath(href)
                      : null;

                    if (guidancePath) {
                      return (
                        <Button
                          type="button"
                          variant="contained"
                          size="small"
                          onClick={() =>
                            navigate(guidancePath, {
                              state: { returnTo: location.pathname },
                            })
                          }
                          sx={{
                            my: 0.75,
                            borderRadius: 2,
                            bgcolor: "#0C8F63",
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": { bgcolor: "#087553" },
                          }}
                        >
                          Open guidance
                        </Button>
                      );
                    }

                    return (
                      <a
                        {...props}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {message.content || ""}
              </Markdown>
            </Box>
          </Box>

          <Stack
            direction="row"
            justifyContent={isUser ? "flex-end" : "flex-start"}
            spacing={0.25}
            sx={{ mt: 0.5, color: "#8CA09A" }}
          >
            <Tooltip title="Copy">
              <IconButton
                size="small"
                aria-label="Copy message"
                onClick={() => navigator.clipboard.writeText(message.content || "")}
                sx={{ color: "inherit" }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ alignSelf: "center" }}>
              Copy
            </Typography>
            {!isUser && onReport ? (
              <Tooltip title="Report">
                <IconButton
                  size="small"
                  aria-label="Report message"
                  onClick={onReport}
                  sx={{ color: "inherit" }}
                >
                  <OutlinedFlagIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>

          {flags.includes("PEARLIQuestion") ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
              {quickDomains.map((domain) => (
                <Box
                  key={domain}
                  component="button"
                  type="button"
                  onClick={() => !disabled && onQuickReply?.(domain)}
                  disabled={disabled || !onQuickReply}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    color: "text.primary",
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.75,
                    cursor: disabled || !onQuickReply ? "not-allowed" : "pointer",
                    "&:hover": { bgcolor: disabled ? "background.paper" : "action.hover" },
                    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {domain}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}

          {flags.includes("oneToFive") ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
              {[1, 2, 3, 4, 5].map((score, index) => (
                <Box
                  key={score}
                  component="button"
                  type="button"
                  onClick={() => !disabled && onQuickReply?.(`${score}: ${oneToFiveLabels[index]}`)}
                  disabled={disabled || !onQuickReply}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    color: "text.primary",
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.75,
                    minWidth: 72,
                    cursor: disabled || !onQuickReply ? "not-allowed" : "pointer",
                    "&:hover": { bgcolor: disabled ? "background.paper" : "action.hover" },
                    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {score}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {oneToFiveLabels[index]}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};

export default ChatMessage;
